import { useQuery } from '@tanstack/react-query';

import { getBackInStockWaitlistItems } from 'api/jalapeno';
import useAccountQuery from 'api/queries/useAccountQuery';
import { BACK_IN_STOCK_WAITLIST_ITEMS_QUERY_KEY } from 'api/queries/useBackInStockWaitlistItemsQuery';
import { FEATURE_FLAG_NAMES, useAmplitudeFeatureFlag } from 'hooks/useFeatureFlag';

/**
 * @param {Object} item
 * @returns {boolean}
 */
export function isActiveWaitlistItem(item) {
    return item?.backInStockWaitlistStatus === 'pending';
}

/**
 * @param {Object[]} [items=[]]
 * @returns {number[]}
 */
export function getWaitlistedItemIds(items = []) {
    return items
        .filter(isActiveWaitlistItem)
        .map((item) => item.id ?? item.sellableItemID)
        .filter((id) => id != null);
}

/**
 * @param {Object[]} items
 * @param {number} sellableItemID
 * @returns {number|null}
 */
export function getWaitlistRequestId(items, sellableItemID) {
    const item = (items ?? [])
        .filter(isActiveWaitlistItem)
        .find((entry) => (entry.id ?? entry.sellableItemID) === sellableItemID);
    return item?.backInStockWaitlistRequestID ?? null;
}

const useWaitlistedItemIdsQuery = ({ queryProps } = {}) => {
    const { data: account } = useAccountQuery();
    const { chargeID } = account?.nextOrder ?? {};
    const { isAssignedVariant: isBackInStockWaitlistEnabled } = useAmplitudeFeatureFlag({
        flagName: FEATURE_FLAG_NAMES.BACK_IN_STOCK_WAITLIST,
        variant: 'on',
    });
    const { enabled: queryEnabled = true, ...restQueryProps } = queryProps ?? {};

    return useQuery({
        queryKey: [BACK_IN_STOCK_WAITLIST_ITEMS_QUERY_KEY, chargeID],
        queryFn: async () => {
            const response = await getBackInStockWaitlistItems(chargeID);
            return response?.data?.items ?? [];
        },
        select: getWaitlistedItemIds,
        enabled: !!chargeID && !!isBackInStockWaitlistEnabled && queryEnabled,
        ...restQueryProps,
    });
};

export default useWaitlistedItemIdsQuery;
